/**
 * Does this order have anything to pay for?
 *
 * Mirrors the rule the BE applies in `FeeProductDeriver`:
 *
 *   chargeable = productsAreChargeable
 *             || MEASUREMENT_ON_SITE in catalogKinds
 *             || any derived fee has a chargeable line
 *
 * The frontend can answer all three without guessing:
 *   - `selectCartTotal` already sums the cart lines AND the display mirror of the
 *     derived fees (organization fee, travel costs), so a non-zero total covers
 *     both the products and the fees;
 *   - the measurement clause has to be explicit, because an On-Site Area Report's
 *     catalogue price is decorative — the BE replaces it with the rate-card
 *     breakdown at persistence, so a 0.00 line still bills. Reading the total
 *     alone would let a real charge through with no method to bill it.
 *
 * When this returns false the billing step asks for no payment method and the
 * order carries none — v1's behaviour, which cleared the method for a free cart.
 *
 * Safe in the one direction that matters: if the frontend says free and the BE
 * disagrees, the order is rejected with a clear message rather than mischarged —
 * and `serializePlacement` still sends a method whenever one is resolvable, which
 * the BE validates and then drops for a free order, so the disagreement is
 * absorbed rather than fatal.
 */

import { useCallback } from 'react'

import { ProductKind } from 'constants/product'
import type { ResolvedCatalogue } from 'models/purchaseFlowRevamp'
import { cartProductKinds } from 'dataQueries/purchaseFlowRevampProductCodes'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'

import { usePfStore } from '../../store'
import type { PfStoreState } from '../../store/store'
import { selectCartTotal } from '../../store/selectors'

/**
 * Takes a BOOLEAN, not the `Big` total, on purpose: `selectCartTotal` returns a
 * fresh `Big` on every call, and feeding that through `usePfStore`
 * (`useSyncExternalStore`) yields a new snapshot reference each render — React's
 * "getSnapshot should be cached" infinite loop. Callers collapse it with `.gt(0)`
 * first. Legacy's `Billing.component.tsx` carries the same warning.
 *
 * @param hasPositiveTotal cart total (incl. the display mirror of derived fees) > 0
 * @param cartKinds product kinds the cart resolves to
 *
 * @example
 * isOrderChargeable(false, ['GROUND_PHOTO'])          // false
 * isOrderChargeable(false, ['MEASUREMENT_ON_SITE'])   // true — priced by rate card
 */
export const isOrderChargeable = (
  hasPositiveTotal: boolean,
  cartKinds: ReadonlyArray<string>
): boolean => {
  if (hasPositiveTotal) return true
  return cartKinds.includes(ProductKind.MEASUREMENT_ON_SITE)
}

/**
 * The same decision over the store and the catalogue: the one rule Billing, the Summary gate, the
 * Stripe decision and the placed order all read, so none of them can treat a cart as free while
 * another charges it.
 */
export const isCartChargeable = (state: PfStoreState, catalogue: ResolvedCatalogue | undefined): boolean =>
  isOrderChargeable(selectCartTotal(state).gt(0), cartProductKinds(state.items, catalogue))

/**
 * The same decision as a hook, for surfaces that do not already hold the catalogue.
 *
 * @example
 * const isFreeCart = useIsFreeCart()
 */
export const useIsFreeCart = (): boolean => {
  const catalogue = usePfCatalogue().data
  // Boolean, never the raw `Big` — see the note on `isOrderChargeable`.
  return !usePfStore(useCallback((s: PfStoreState) => isCartChargeable(s, catalogue), [catalogue]))
}

/**
 * The OneSystem quota covers the whole cart: at least one line is covered and nothing is left to
 * charge. Placement then sends no billing method, which the backend requires for a covered order.
 */
export const selectCoveredByQuota = (state: PfStoreState): boolean =>
  state.items.some((item) => item.priceSnapshot?.quotaCovered === true) && !selectCartTotal(state).gt(0)
